/**
 * Access Request Card Component
 *
 * Displays individual access request details with approve/deny actions
 */

import React from 'react';
import type { AccessRequest } from '../../../types/access-control';

interface AccessRequestCardProps {
  request: AccessRequest;
  onApprove?: (request: AccessRequest) => void;
  onDeny?: (request: AccessRequest) => void;
  onDelete?: (request: AccessRequest) => void;
}

export const AccessRequestCard: React.FC<AccessRequestCardProps> = ({
  request,
  onApprove,
  onDeny,
  onDelete,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = () => {
    switch (request.status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Approved
          </span>
        );
      case 'denied':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Denied
          </span>
        );
    }
  };

  const isPending = request.status === 'pending';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-gray-900">
              {request.name || 'Unknown User'}
            </h3>
            {getStatusBadge()}
          </div>
          <p className="text-sm text-gray-600">{request.email}</p>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-start gap-2">
          <span className="text-xs font-medium text-gray-500 w-24">Requested:</span>
          <span className="text-xs text-gray-700">{formatDate(request.createdAt)}</span>
        </div>

        {request.oauthProvider && (
          <div className="flex items-start gap-2">
            <span className="text-xs font-medium text-gray-500 w-24">Provider:</span>
            <span className="text-xs text-gray-700 capitalize">{request.oauthProvider}</span>
          </div>
        )}

        {request.reason && (
          <div className="flex items-start gap-2">
            <span className="text-xs font-medium text-gray-500 w-24">Reason:</span>
            <span className="text-xs text-gray-700">{request.reason}</span>
          </div>
        )}

        {request.reviewedBy && (
          <>
            <div className="flex items-start gap-2">
              <span className="text-xs font-medium text-gray-500 w-24">Reviewed by:</span>
              <span className="text-xs text-gray-700">{request.reviewedBy}</span>
            </div>
            {request.reviewedAt && (
              <div className="flex items-start gap-2">
                <span className="text-xs font-medium text-gray-500 w-24">Reviewed at:</span>
                <span className="text-xs text-gray-700">{formatDate(request.reviewedAt)}</span>
              </div>
            )}
          </>
        )}

        {request.reviewNotes && (
          <div className="flex items-start gap-2">
            <span className="text-xs font-medium text-gray-500 w-24">Review notes:</span>
            <span className="text-xs text-gray-700">{request.reviewNotes}</span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {(isPending && (onApprove || onDeny) || onDelete) && (
        <div className="pt-3 border-t border-gray-200 space-y-2">
          {/* Approve/Deny for pending */}
          {isPending && (onApprove || onDeny) && (
            <div className="flex gap-2">
              {onApprove && (
                <button
                  onClick={() => onApprove(request)}
                  className="flex-1 px-4 py-2 text-sm font-semibold bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                >
                  Approve
                </button>
              )}
              {onDeny && (
                <button
                  onClick={() => onDeny(request)}
                  className="flex-1 px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Deny
                </button>
              )}
            </div>
          )}

          {/* Delete button for all statuses */}
          {onDelete && (
            <button
              onClick={() => onDelete(request)}
              className="w-full px-4 py-2 text-sm font-semibold bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              Delete Request
            </button>
          )}
        </div>
      )}
    </div>
  );
};
